/**
 * Comparing a local folder with a Lattice project.
 *
 * WHY THIS IS SIMPLER THAN IT LOOKS
 *   Engineering files are opaque binaries; there is no merge. So the only
 *   question for each path is "which single version wins", never "how do these
 *   combine". And because Lattice has locks, the common case is arranged in
 *   advance: you lock a part before editing it, so nobody can change the
 *   remote copy while you work, and pushing it afterwards cannot conflict.
 *   Conflicts are the exception — what happens when somebody edits without
 *   locking — rather than the normal case a merge algorithm has to survive.
 *
 * THE COMPARISON
 *   Three hashes per path, exactly as version control does it:
 *     base   what we recorded on disk at the end of the last sync
 *     local  what is on disk now
 *     remote what Lattice holds now
 *   `base` is the pivot that turns "they differ" into "who changed it".
 *   Without it, a file the colleague added and a file you deleted look the
 *   same, and the tool has to guess. With it, neither is ambiguous.
 *
 * DATA DEFINITIONS
 *
 *   A Side is "local" | "remote" | "both" | "neither".
 *   Interpretation: who has touched this path since `base`.
 *
 *   An ItemState is one of:
 *     "unchanged"     nothing to do
 *     "pull-new"      it exists remotely and not here
 *     "pull-update"   it changed remotely, not here
 *     "pull-delete"   it was deleted remotely, unchanged here
 *     "push-new"      it exists here and not remotely
 *     "push-update"   it changed here, not remotely
 *     "push-delete"   it was deleted here, unchanged remotely
 *     "conflict"      both sides changed it, to different content
 *     "converged"     both changed it to the SAME content; only the base is stale
 *     "blocked"       somebody else holds the lock, so we must not push
 *   Interpretation: what a sync would do to one path, before the person has
 *   chosen anything.
 *
 *   A Resolution is "accept-remote" | "keep-local" | "skip".
 *   Interpretation: what the person decided for a path. Every item carries a
 *   DEFAULT, and the defaults are chosen so that pressing the obvious button
 *   without reading does the safe thing.
 *
 *   A SyncItem is one path with its three hashes, its state, its default and
 *   (once chosen) its resolution.
 *
 *   A SyncPlan is the list of items plus counts. Interpretation: everything a
 *   person needs to decide, and everything the executor needs to act.
 */
import {} from './path.js';
import {} from './change.js';
import {} from './user.js';
import { refusedBy } from './lock.js';
const sameContent = (a, b) => (a === undefined && b === undefined) || (a !== undefined && b !== undefined && a.sha === b.sha);
/**
 * Decide what one path needs, given the three views of it.
 *
 * Written as an explicit table rather than nested conditionals because the
 * cases are the specification: every combination of "changed here" and
 * "changed there" has exactly one right answer, and the reader should be able
 * to check them off.
 * classify : FileFacts? FileFacts? FileFacts? -> {state, suggested}
 */
export function classify(base, local, remote) {
    const localChanged = !sameContent(base, local);
    const remoteChanged = !sameContent(base, remote);
    if (!localChanged && !remoteChanged)
        return { state: 'unchanged', suggested: 'skip' };
    if (!localChanged && remoteChanged) {
        if (!remote)
            return { state: 'pull-delete', suggested: 'accept-remote' };
        return { state: base ? 'pull-update' : 'pull-new', suggested: 'accept-remote' };
    }
    if (localChanged && !remoteChanged) {
        if (!local)
            return { state: 'push-delete', suggested: 'keep-local' };
        return { state: base ? 'push-update' : 'push-new', suggested: 'keep-local' };
    }
    // Both sides moved. If they landed on the same content there is nothing to
    // transfer — only our record of the base is out of date.
    if (sameContent(local, remote))
        return { state: 'converged', suggested: 'skip' };
    // A genuine disagreement. The default is to do NOTHING: choosing for the
    // person here is how synchronisation tools lose work.
    return { state: 'conflict', suggested: 'skip' };
}
/**
 * Build the plan for a whole project.
 *
 * `locks` is consulted only to mark outgoing changes we must not make: if
 * somebody else holds a file, pushing over it would defeat the point of their
 * having locked it. Incoming changes are never blocked — receiving somebody
 * else's work is always safe.
 *
 * buildSyncPlan : {projectId, base, local, remote, locks, me} -> SyncPlan
 */
export function buildSyncPlan(input) {
    const { projectId, base, local, remote, locks, me } = input;
    const paths = new Set([...base.keys(), ...local.keys(), ...remote.keys()]);
    const items = [];
    for (const key of [...paths].sort()) {
        const { state, suggested } = classify(base.get(key), local.get(key), remote.get(key));
        if (state === 'unchanged')
            continue;
        const path = (local.get(key) ?? remote.get(key) ?? base.get(key)) ? key : key;
        const outgoing = state === 'push-new' || state === 'push-update' || state === 'push-delete';
        const lock = outgoing && locks && me ? refusedBy(locks, projectId, path, me) : undefined;
        items.push({
            path,
            state: lock ? 'blocked' : state,
            ...(base.get(key) ? { base: base.get(key) } : {}),
            ...(local.get(key) ? { local: local.get(key) } : {}),
            ...(remote.get(key) ? { remote: remote.get(key) } : {}),
            suggested: lock ? 'skip' : suggested,
            ...(lock ? { lockedBy: lock.userId } : {}),
        });
    }
    const count = (pred) => items.filter(pred).length;
    return {
        projectId,
        items,
        incoming: count(i => i.state.startsWith('pull')),
        outgoing: count(i => i.state.startsWith('push')),
        conflicts: count(i => i.state === 'conflict'),
        blocked: count(i => i.state === 'blocked'),
        clean: items.length === 0,
    };
}
/** actionFor : SyncItem Resolution -> Action */
export function actionFor(item, resolution) {
    // A converged file is settled BEFORE the resolution is consulted, because
    // there is no decision to make: both sides already hold the same bytes, and
    // only our record of the base is stale. Honouring a "skip" here would leave
    // the base unchanged and the file would be reported as needing attention on
    // every future sync, forever.
    if (item.state === 'converged')
        return 'record-only';
    if (item.state === 'blocked')
        return 'nothing';
    if (resolution === 'skip')
        return 'nothing';
    if (resolution === 'accept-remote') {
        return item.remote ? 'download' : 'delete-local';
    }
    // keep-local
    return item.local ? 'upload' : 'delete-remote';
}
/**
 * Apply the suggested resolution to every item.
 *
 * This is what the plain "Pull" and "Push" buttons do — the person who just
 * wants the safe thing should not have to tick anything. Conflicts default to
 * "skip", so neither button can silently overwrite work.
 * suggestAll : SyncPlan -> Map<Path, Resolution>
 */
export function suggestAll(plan) {
    return new Map(plan.items.map(i => [i.path, i.suggested]));
}
/** Only the incoming half, for a "Pull" that leaves local work alone. */
export function pullOnly(plan) {
    return new Map(plan.items.map(i => [i.path, i.state.startsWith('pull') ? 'accept-remote' : 'skip']));
}
/** Only the outgoing half, for a "Push" that does not disturb local files. */
export function pushOnly(plan) {
    return new Map(plan.items.map(i => [i.path, i.state.startsWith('push') ? 'keep-local' : 'skip']));
}
/**
 * The manifest after a set of resolutions has been carried out.
 *
 * Interpretation: the new pivot for next time. A path we skipped keeps its old
 * base — deliberately, so a conflict we declined to settle is still a conflict
 * tomorrow rather than quietly becoming "unchanged".
 * nextManifest : Manifest SyncPlan Map<Path,Resolution> -> Manifest
 */
export function nextManifest(base, plan, resolutions) {
    const next = new Map(base);
    for (const item of plan.items) {
        const action = actionFor(item, resolutions.get(item.path) ?? 'skip');
        switch (action) {
            case 'nothing': continue;
            case 'download':
            case 'record-only':
                if (item.remote)
                    next.set(item.path, item.remote);
                else
                    next.delete(item.path);
                continue;
            case 'upload':
                if (item.local)
                    next.set(item.path, item.local);
                continue;
            case 'delete-local':
            case 'delete-remote':
                next.delete(item.path);
                continue;
        }
    }
    return next;
}
/** A short sentence describing what an item is. describeItem : SyncItem -> String */
export function describeItem(item) {
    switch (item.state) {
        case 'pull-new': return 'new here';
        case 'pull-update': return 'changed in Lattice';
        case 'pull-delete': return 'deleted in Lattice';
        case 'push-new': return 'new on this machine';
        case 'push-update': return 'changed on this machine';
        case 'push-delete': return 'deleted on this machine';
        case 'conflict': return 'changed in both places';
        case 'converged': return 'already the same';
        case 'blocked': return `locked by ${item.lockedBy ?? 'someone else'}`;
        case 'unchanged': return 'up to date';
    }
}
//# sourceMappingURL=syncplan.js.map